import * as Forms                       from '../../components/forms';
import * as Phone                       from 'libphonenumber-js';
import { completeVerification }         from './gmb_api';
import { Fragment }                     from 'react';
import { getVerificationAddressString } from './display_helpers';
import { requestVerification }          from './gmb_api';
import { useCallback }                  from 'react';
import { useEffect }                    from 'react';
import { useRef }                       from 'react';
import { useState }                     from 'react';
import { verificationMethods }          from './gmb_api';
import React                            from 'react';
import renderAsPopup                    from '../../components/render_as_popup';
import rollbar                          from '../../rollbar';
import StandaloneButton                 from '../../components/standalone_button';
import Swal                             from 'sweetalert2';
import useObjectState                   from '../../hooks/use_object_state';


export default renderAsPopup(VerifyModal);

function VerifyModal({
  accessToken,
  userName,
  business,
  googleLocation,
  updateGoogleLocation,
  verifyOption,
  navigateBackTo,
  popup
}) {

  const [ state, updateState ] = useObjectState({
    onClickClose: () => {
      popup.close();
    }
  });

  function setOnClickClose(onClickClose) {
    updateState({ onClickClose });
  }

  return (
    <div className="verification-option-modal">
      <div className="top-section">
        <div className="block inset">
          <h2><ModalTitle {...{ verifyOption }}/></h2>
        </div>
        <ModalContent {...{
          accessToken,
          userName,
          business,
          googleLocation,
          updateGoogleLocation,
          verifyOption,
          navigateBackTo,
          setOnClickClose,
          popup
        }}/>
      </div>
      <div className="bottom-section">
        <StandaloneButton label="Close" onClick={state.onClickClose}/>
      </div>
    </div>
  );
}


function ModalTitle({ verifyOption }) {
  const { verificationMethod } = verifyOption;
  switch (verificationMethod) {
    case verificationMethods.SMS:
      return 'SMS Verification';
    case verificationMethods.PHONE_CALL:
      return 'Phone Verification';
    case verificationMethods.EMAIL:
      return 'Email Verification';
    case verificationMethods.ADDRESS:
      return 'Postcard Verification';
    default:
      return null;
  }
}

function ModalContent({
  accessToken,
  userName,
  business,
  googleLocation,
  updateGoogleLocation,
  verifyOption,
  navigateBackTo,
  setOnClickClose,
  popup
}) {
  const [ state, updateState ] = useObjectState({
    status:              'unconfirmed',
    pendingVerification: undefined,
    error:               undefined
  });

  const { status }              = state;
  const { pendingVerification } = state;

  async function requestPin(contactName = userName) {
    updateState({ status: 'requestingPin' });

    try {
      const response = await requestVerification({ accessToken, contactName, googleLocation, verifyOption });
      updateState({
        status:              'success',
        pendingVerification: response
      });
    } catch (error) {
      rollbar.error(error);
      updateState({
        status: 'error'
      });
    }
  }

  return (
    <Fragment>
      {status === 'unconfirmed' && <Confirmation {...{ userName, verifyOption, requestPin }}/>}
      {status === 'requestingPin' && <PinRequestPending/>}
      {status === 'error' && <PinRequestFailed/>}
      {status === 'success' && <PinRequestSucceeded {...{
        accessToken,
        pendingVerification,
        business,
        googleLocation,
        updateGoogleLocation,
        verifyOption,
        navigateBackTo,
        setOnClickClose,
        requestPin,
        popup
      }}/>}
    </Fragment>
  );
}


function Confirmation({ userName, verifyOption, requestPin }) {
  const { verificationMethod } = verifyOption;

  if (verificationMethod === verificationMethods.ADDRESS)
    return <PostcardConfirmation {...{ userName, verifyOption, requestPin }}/>;

  return (
    <div className="verify-confirm">
      <div className="block inset">
        <ConfirmationText {...{ userName, verifyOption }}/>
      </div>
      <div className="block inset">
        <p>
          <button className="button button-fill" onClick={requestPin}>
            <ConfirmationButtonLabel {...{ verifyOption }}/>
          </button>
        </p>
      </div>
    </div>
  );
}

function ConfirmationText({ verifyOption }) {
  const { verificationMethod } = verifyOption;
  switch (verificationMethod) {

    case verificationMethods.SMS: {
      const { phoneNumber } = verifyOption.phoneData;
      const formattedPhone  = Phone.format(phoneNumber, 'National');
      return <p>Google will text a PIN to {formattedPhone}.</p>;
    }

    case verificationMethods.PHONE_CALL: {
      const { phoneNumber } = verifyOption.phoneData;
      const formattedPhone  = Phone.format(phoneNumber, 'National');
      return <p>Google will call you with a PIN at {formattedPhone}.</p>;
    }

    case verificationMethods.EMAIL: {
      const { userName: emailUserName } = verifyOption.emailData;
      const { domainName }              = verifyOption.emailData;
      const emailAddress                = `${emailUserName}@${domainName}`;
      return <p>Google will email a PIN to {emailAddress}.</p>;
    }

    default:
      return null;
  }
}

function ConfirmationButtonLabel({ verifyOption }) {
  const { verificationMethod } = verifyOption;
  switch (verificationMethod) {
    case verificationMethods.SMS:
      return 'Text Me';
    case verificationMethods.PHONE_CALL:
      return 'Call Me';
    case verificationMethods.EMAIL:
      return 'Email Me';
    default:
      return null;
  }
}

function PostcardConfirmation({ userName, verifyOption, requestPin }) {
  const streetAddress         = getVerificationAddressString(verifyOption);
  const [ record, setRecord ] = useState({ userName });

  function save(contactData) {
    setRecord(contactData);
    requestPin(contactData.userName);
  }

  return (
    <div className="verify-confirm">
      <div className="list block inset">
        <p>Google will send a postcard with a verification PIN to {streetAddress}.</p>
        <Forms.Form {...{ record, save }}>
          <ContactFormFields/>
        </Forms.Form>
      </div>
    </div>
  );
}

function ContactFormFields({ ...form }) {
  return (
    <div className="list">
      <ul>
        <Forms.Error {...form}/>
        <Forms.Input
          {...form}
          name="userName"
          type="text"
          label="Contact Name"
          required={true}
          autoComplete="off"/>
        <Forms.Button {...form} label="Send Me A Postcard"/>
      </ul>
    </div>
  );
}


function PinRequestPending() {
  return (
    <div className="block inset">
      <p>
        Requesting verification PIN…
      </p>
    </div>
  );
}

function PinRequestFailed() {
  return (
    <div className="block inset">
      <p>
        There was an error requesting the verification PIN from Google. Please try a different method, or try again later.
      </p>
    </div>
  );
}

function PinRequestSucceeded(props) {
  const { verifyOption }       = props;
  const { verificationMethod } = verifyOption;

  if (verificationMethod === verificationMethods.ADDRESS)
    return <AddressComplete {...props}/>;

  return <PinCollectionView {...props}/>;
}


function AddressComplete({
  business,
  navigateBackTo,
  setOnClickClose,
  popup
}) {
  useEffect(() => {
    setOnClickClose(() => {
      popup.close();
      navigateBackTo(`/${business.id}/settings/business_profile`, { force: true });
    });
  }, [ business.id, navigateBackTo, popup, setOnClickClose ]);
  return (
    <div className="address-complete">
      <div className="block inset">
        <p>In 5-7 business days you will receive a postcard from Google with your PIN. Follow the instructions on the postcard to enter your PIN. Then return here to check on the status of your listing.</p>
      </div>
    </div>
  );
}


const numeric = /^\d*$/;

function PinCollectionView({
  accessToken,
  pendingVerification,
  googleLocation,
  updateGoogleLocation,
  verifyOption,
  navigateBackTo,
  requestPin,
  popup
}) {
  const [ state, updateState ] = useObjectState({
    status:         'enteringPin',
    value:          '',
    showNewPinLink: false,
    error:          undefined
  });

  const ref                = useRef();
  const { status }         = state;
  const { value }          = state;
  const { error }          = state;
  const { showNewPinLink } = state;
  const buttonDisabled     = status === 'pending';

  const onSubmit = useCallback(async event => {
    event.preventDefault();
    updateState({ status: 'completionPending' });

    try {
      await completeVerification({ accessToken, pendingVerification, pin: value });

      const updatedLocation = {
        ...googleLocation,
        locationState: {
          ...googleLocation.locationState,
          isVerified: true
        }
      };

      // If the updateGoogleLocation call fails, we don't want to surface that
      // in the Verify Modal UI, as there's nothing the user can do about it.
      try {
        await updateGoogleLocation(updatedLocation);
      } catch (updateLocationError) {
        rollbar.error(updateLocationError);
      }

      popup.close();
      navigateBackTo('../');
      Swal.fire({
        text: 'Your location has been connected!',
        icon: 'success'
      });

    } catch (completeVerificationError) {
      updateState({
        status:         'enteringPin',
        error:          completeVerificationError,
        showNewPinLink: true
      });
    }
  }, [ accessToken, googleLocation, navigateBackTo, pendingVerification, popup, updateGoogleLocation, updateState, value ]);

  const onChange = useCallback(event => {
    const val = event.target.value;
    if (numeric.test(val)) {
      updateState({
        value: val,
        error: undefined
      });
    }
  }, [ updateState ]);

  useEffect(() => {
    if (ref.current)
      ref.current.focus();
  }, [ error ]);

  return (
    <div className="verify-pin">
      <form onSubmit={onSubmit} className="pin-form">

        {status === 'enteringPin' && (
          <div className="block inset">
            <p className="instructions">
              <PinFormInstructions {...{ verifyOption }}/>
            </p>
            <p>
              <input
                value={value}
                onChange={onChange}
                ref={ref}
                id="pin-field"
                type="text"
                pattern="[0-9]*"
                size={6}
                autoComplete="off"
              />
            </p>
            {error && <PinInputError {...{ error }}/>}
            {showNewPinLink && <RequestNewPinLink {...{ requestPin }}/>}
          </div>
        )}

        {status === 'completionPending' && <CompletionPending/>}
        {status === 'requestingPin' && <PinRequestPending/>}

        <div className="block inset">
          <button
            type="submit"
            className="button button-fill"
            disabled={buttonDisabled}
          >
            Verify Location
          </button>
        </div>

      </form>
    </div>
  );
}

function PinFormInstructions({ verifyOption }) {
  const { verificationMethod } = verifyOption;
  switch (verificationMethod) {
    case verificationMethods.SMS:
      return 'You should receive a text from Google. Enter the PIN provided below.';
    case verificationMethods.PHONE_CALL:
      return 'You should receive a phone call from Google. Enter the PIN provided below.';
    case verificationMethods.EMAIL:
      return 'You should receive an email from Google. Enter the PIN provided below.';
    default:
      return null;
  }
}

function PinInputError({ error }) {
  const message = getPinErrorMessage(error);
  return (
    <p className="pin-input-error">
      {message}
    </p>
  );
}

function getPinErrorMessage(error) {
  const type = getPinErrorType(error);

  switch (type) {
    case 'Invalid PIN.':
      return 'That doesn’t look like the correct PIN number.';
    case 'unknown':
      return 'An error occurred. Please try again.';
    default:
      return type;
  }
}

function getPinErrorType(error) {
  try {
    return error.message;
  } catch (err) {
    return 'unknown';
  }
}

function CompletionPending() {
  return (
    <div className="block inset">
      <p>
        Completing verification…
      </p>
    </div>
  );
}

function RequestNewPinLink({ requestPin }) {
  async function onClick(event) {
    event.preventDefault();
    requestPin();
  }

  return (
    <p>
      <a href="#" onClick={onClick}>Request a new PIN</a>
    </p>
  );

}
